/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* Imports */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

import "intersection-observer";
import "lazysizes";
import {
  TweenMax,
  TimelineMax,
  ScrollToPlugin,
  Draggable,
  CSSPlugin,
} from "gsap/all";

import { ThrowPropsPlugin } from "./ThrowPropsPlugin";

import { debounce, addListenerMulti, convertRemToPixels } from "./helpers";

// Initialize Plugins to avoid tree shaking issues
const plugins = [ScrollToPlugin, CSSPlugin, ThrowPropsPlugin];
const axios = require("axios");

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* Variables */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

const header = document.querySelector("header"),
  body = document.querySelector("body"),
  main = document.querySelector("main"),
  footer = document.querySelector("footer");

let isBottom = false;

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* Lazy Event (Bugfix Safari) */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

document.addEventListener("lazybeforeunveil", function (e) {
  window.dispatchEvent(new Event("resize"));
});

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* Remove Hash from window location href (set by navigating back from project) */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
function removeHash() {
  if (window.location.hash !== "") {
    TweenMax.to(window, 2, {
      scrollTo: {
        y: window.location.hash,
        offsetY: 0,
        autoKill: true,
      },
      onComplete: function () {
        history.replaceState(
          "",
          document.title,
          window.location.pathname + window.location.search
        );
      },
      ease: Power4.easeOut,
    });
  }
}

document.addEventListener("DOMContentLoaded", removeHash);

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* Intro */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

function introFlash() {
  if (
    document.body.classList.contains("page--home") &&
    !window.location.hash.length
  ) {
    flash();
    setTimeout(function () {
      flash();
    }, 100);
  }
}

function highlightContent() {
  let offset = window.innerHeight / 10;

  if (
    globalScrollTop < offset &&
    document.body.classList.contains("page--home")
  ) {
    TweenMax.to(window, 2, {
      scrollTo: {
        y: parseInt(offset, 10),
        offsetY: 0,
        autoKill: true,
      },
      ease: Power4.easeOut,
    });
  }
}

window.addEventListener("load", function () {
  setTimeout(function () {
    // introFlash();
    highlightContent(true);
  }, 1000);
});

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* Flash */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

function flash() {
  document.body.classList.add("flash");
  setTimeout(function () {
    document.body.classList.remove("flash");
  }, 50);
}

document.body.addEventListener("click", function (e) {
  event.preventDefault();

  if (
    event.target.tagName.toLowerCase() === ".viewer" ||
    event.target.closest(".viewer") !== null
  ) {
    return;
  }

  let targetURL;

  if (event.target.tagName.toLowerCase() === "a") {
    targetURL = event.target.getAttribute("href");
  } else if (event.target.closest("a") !== null) {
    targetURL = event.target.closest("a");
  } else {
    targetURL = false;
  }

  flash();

  if (targetURL) {
    setTimeout(function () {
      if (e.target.getAttribute("target") === "_blank") {
        window.open(targetURL, "_blank");
      } else {
        window.location = targetURL;
      }
    }, 150);
  }
});

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* Update Scroll Position */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

Number.prototype.map = function (in_min, in_max, out_min, out_max) {
  return ((this - in_min) * (out_max - out_min)) / (in_max - in_min) + out_min;
};

function clamp(num, min, max) {
  return num <= min ? min : num >= max ? max : num;
}

const containers = document.querySelectorAll(".container");

let globalScrollTop;

const getScrollTop = function (el) {
  // Use globalScrollTop because it's faster than getBoundingClientRect()
  if (!el) {
    return (document.scrollingElement || document.documentElement).scrollTop;
  } else {
    return el.scrollTop;
  }
};

const loop = function (style, previousScrollTop) {
  // Continue loop
  const repeat = () => {
    // It depends on the browser, but it turns out that closures
    // are sometimes faster than .bind or .apply.
    requestAnimationFrame(() => loop(style, previousScrollTop));
  };

  globalScrollTop = getScrollTop();

  containers.forEach((container) => {
    if (document.body.classList.contains("horizontal_animation--active")) {
      let dist;
      if (container.tagName === "HEADER") {
        dist = container.offsetTop - globalScrollTop;
      } else {
        dist = container.offsetTop - globalScrollTop + window.innerHeight;
        let distPc = (-dist / window.innerHeight) * 100;
        let offsetX = distPc.map(-50, 50, 0, 10);
        offsetX = clamp(offsetX, 0, 10);
        let transformValueImg = `translate3D(${-offsetX}%, 0, 0)`;
        let img = container.querySelectorAll("img")[0];
        let video = container.querySelectorAll("video")[0];
        if (img) {
          img.style.transform = transformValueImg;
        }
        if (video) {
          video.style.transform = transformValueImg;
        }
      }
    }
  });

  if (
    globalScrollTop >= document.body.offsetHeight - window.innerHeight &&
    globalScrollTop >= window.innerHeight &&
    document.body.offsetHeight > window.innerHeight
  ) {
    if (
      document.body.classList.contains("loop--active") &&
      document.body.classList.contains("page--home")
    ) {
      scroll(0, 0);
      document.querySelectorAll(".accordion").forEach((accordion) => {
        closeAccordion(accordion);
      });
    } else {
      if (!document.body.classList.contains("is--bottom")) {
        flash();
        document.body.classList.add("is--bottom");
      }
    }
  } else {
    if (document.body.classList.contains("is--bottom")) {
      document.body.classList.remove("is--bottom");
    }
  }

  // Only continue when globalScrollTop has changed
  if (previousScrollTop === globalScrollTop) return repeat();
  else previousScrollTop = globalScrollTop;

  repeat();
};

loop();

function initCloneColorChange() {
  if (document.body.classList.contains("page--home")) {
    let io = new IntersectionObserver(
      (entries) => {
        if (entries[0].intersectionRatio >= 1) {
          document.body.classList.add("intro--active");
        } else {
          document.body.classList.remove("intro--active");
        }
      },
      {
        threshold: [0, 0.5, 1],
      }
    );
    // Start observing an element
    io.observe(document.querySelector("#clone"));
  }
}

document.addEventListener("DOMContentLoaded", initCloneColorChange);

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* Screenshot */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

function initScreenshot() {
  if (document.body.contains(document.querySelector("#about"))) {
    let screenshot = document.createElement("div"),
      cloneWrapper = document.createElement("div"),
      cloneAbout = document.querySelector("#about").cloneNode(true),
      cloneFooter = document.querySelector("footer").cloneNode(true),
      cloneClone = document.querySelector("#clone").cloneNode(true);
    screenshot.setAttribute("id", "screenshot");
    cloneWrapper.setAttribute("id", "screenshot__wrapper");
    cloneWrapper.append(cloneAbout, cloneFooter, cloneClone);
    screenshot.append(cloneWrapper);
    body.appendChild(screenshot);

    const ioHeader = new IntersectionObserver(
      (entries) => {
        inHeader = entries[0].intersectionRatio < 0.9 ? false : true;
      },
      {
        threshold: 0.9,
      }
    );
    // Start observing an element
    ioHeader.observe(header);

    const ioAbout = new IntersectionObserver(
      (entries) => {
        inAbout = entries[0].intersectionRatio > 0.1 ? true : false;
      },
      {
        threshold: 0.1,
      }
    );
    // Start observing an element
    ioAbout.observe(document.querySelector("#about"));

    if (document.body.classList.contains("loop--active")) {
      const ioClone = new IntersectionObserver(
        (entries) => {
          inClone = entries[0].intersectionRatio > 0.1 ? true : false;
        },
        {
          threshold: 0.1,
        }
      );
      // Start observing an element
      ioClone.observe(document.querySelector("#clone"));
    }

    let inHeader = false,
      inAbout = false,
      inClone = false;

    let lastScrollTop = 0;

    window.addEventListener(
      "scroll",
      function () {
        let st = window.pageYOffset || document.documentElement.scrollTop,
          screenshot = document.querySelector("#screenshot");

        if (st > lastScrollTop) {
          document.documentElement.classList.remove("dir--up");
        } else {
          document.documentElement.classList.add("dir--up");
        }
        lastScrollTop = st <= 0 ? 0 : st; // For Mobile or negative scrolling
        if (document.body.contains(document.querySelector("#about"))) {
          if (inAbout || inHeader || inClone) {
            screenshot && screenshot.classList.remove("is--visible");
          } else {
            screenshot && screenshot.classList.add("is--visible");
          }
          //console.log(inHeader, inAbout);
        }
      },
      false
    );
  }
}

document.addEventListener("DOMContentLoaded", initScreenshot);

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* About/Up Arrow */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

function scrollTo() {
  TweenMax.to(window, 1, {
    scrollTo: {
      y: "#about",
      offsetY: 0,
      autoKill: false,
    },
    onComplete: function () {},
  });
}

function initScroll() {
  let intersectionRatio = 0;
  const elScreenshot = document.querySelector("#screenshot");
  if (elScreenshot) {
    if (document.body.classList.value.includes("single--")) {
      elScreenshot.addEventListener("click", function () {
        window.location.href = elScreenshot.dataset.href;
      });
    }
    if (document.body.contains(document.querySelector("#about"))) {
      elScreenshot.addEventListener("click", scrollTo);
    }
  }
}

document.addEventListener("DOMContentLoaded", initScroll);

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* Project Footer Effect */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

function setHeightFooter() {
  document.documentElement.style.setProperty(
    "--heightFooter",
    `${document.querySelector("footer").offsetHeight}px`
  );
}

document.addEventListener("DOMContentLoaded", setHeightFooter);
window.addEventListener("resize", setHeightFooter);

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* Project Footer Effect */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

function setHeightAbout() {
  if (document.body.contains(document.querySelector("#about"))) {
    document.documentElement.style.setProperty(
      "--heightAbout",
      `${document.querySelector("#about").offsetHeight}px`
    );
  }
}

document.addEventListener("DOMContentLoaded", setHeightAbout);
window.addEventListener("resize", setHeightAbout);

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* 3D Viewer */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

import * as THREE from "three-full";

function setup3D(container, obj) {
  let scene = new THREE.Scene();
  scene.background = new THREE.Color(0xdedede);

  let res = container.offsetWidth / container.offsetHeight;
  let camera = new THREE.PerspectiveCamera(75, res, 0.1, 1000);
  //camera.position.z = 3;

  let loader = new THREE.OBJLoader();
  let url = obj;

  let box, objectLength, objectWidth, objectHeight;

  let renderer = new THREE.WebGLRenderer({
    antialias: true,
  });
  renderer.setPixelRatio(window.devicePixelRatio ? window.devicePixelRatio : 1);
  renderer.setSize(container.offsetWidth, container.offsetHeight);
  container.appendChild(renderer.domElement);

  let controls = new THREE.OrbitControls(camera, renderer.domElement);
  //   console.log(controls);
  controls.autoRotate = false;
  controls.autoRotateSpeed = 1;
  controls.enableDamping = true;
  controls.dampingFactor = 0.25;
  controls.enableZoom = false;
  controls.enablePan = false;

  loader.load(
    url,
    // called when resource is loaded
    (object) => {
      scene.add(object);

      let boundingbox = new THREE.Box3().setFromObject(object);

      let sceneRadiusForCamera =
        (Math.max(
          boundingbox.max.y - boundingbox.min.y,
          boundingbox.max.z - boundingbox.min.z,
          boundingbox.max.x - boundingbox.min.x
        ) /
          2) *
        (1 + Math.sqrt(1)); // golden number to beautify display

      object.rotation.z = 0;
      controls.reset();
      camera.position.z = 0;
      camera.position.y = 0;
      camera.position.x = sceneRadiusForCamera;
      camera.lookAt(scene.position);

      let sizeX = boundingbox.max.x - boundingbox.min.x;
      let sizeY = boundingbox.max.y - boundingbox.min.y;
      let sizeZ = boundingbox.max.z - boundingbox.min.z;

      // Repositioning object
      object.position.x = -boundingbox.min.x - sizeX / 2;
      object.position.y = -boundingbox.min.y - sizeY / 2;
      object.position.z = -boundingbox.min.z - sizeZ / 2;

      object.rotation.z = 0;

      renderer.render(scene, camera);
    },
    // called when loading is in progresses
    (xhr) => {
      let amount = Math.round((xhr.loaded / xhr.total) * 100);
      //   console.log(`${amount}% loaded`);
      container.querySelectorAll(".indicator")[0].innerHTML =
        amount < 100 ? `${amount}% loaded` : "";
    },
    // called when loading has errors
    () => {
      console.log("An error happened");
    }
  );

  var ambient = new THREE.AmbientLight(0xffffff, 0.15);
  var backLight = new THREE.DirectionalLight(0xffffff, 0.3);
  var keyLight = new THREE.DirectionalLight(new THREE.Color("#EEEEEE"), 0.3);
  var fillLight = new THREE.DirectionalLight(new THREE.Color("#EEEEEE"), 0.2);
  keyLight.position.set(-100, 0, 100);
  fillLight.position.set(100, 0, 100);
  backLight.position.set(100, 0, -100).normalize();
  var hemiLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 0.6);
  hemiLight.groundColor.setHSL(0.095, 0, 0.55);
  hemiLight.position.set(0, 100, 0);
  scene.add(hemiLight);
  scene.add(ambient);
  scene.add(keyLight);
  scene.add(fillLight);
  scene.add(backLight);
  scene.lights = {
    keyLight: keyLight,
    fillLight: fillLight,
    backLight: backLight,
    ambient: ambient,
  };

  var animate = function () {
    controls.update();
    requestAnimationFrame(animate);
    renderer.render(scene, camera);
  };

  animate();

  window.addEventListener("resize", function () {
    camera.aspect = container.offsetWidth / container.offsetHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(container.offsetWidth, container.offsetHeight);
  });
}

function init3D() {
  const viewers = document.querySelectorAll(".viewer--3d");

  if (document.body.contains(viewers[0])) {
    viewers.forEach((viewer) => {
      let obj = viewer.dataset.obj;
      let mtl = viewer.dataset.mtl;
      let btnViewer = viewer
        .closest(".component")
        .querySelectorAll(".btn__viewer")[0];

      viewer.addEventListener("click", function fn(e) {
        // console.log("3d");
        viewer.closest(".component").classList.add("viewer--active");
        setup3D(viewer, obj);
        e.currentTarget.removeEventListener(e.type, fn);
      });
    });
  }
}

document.addEventListener("DOMContentLoaded", init3D);

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* Equirectangular Viewer */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

function degrees_to_radians(degrees) {
  let pi = Math.PI;
  return degrees * (pi / 180);
}

function setupEqui(container, obj) {
  var mesh;

  var camera, scene, renderer;

  var isUserInteracting = false,
    onMouseDownMouseX = 0,
    onMouseDownMouseY = 0,
    lon = 0,
    onMouseDownLon = 0,
    lat = 0,
    onMouseDownLat = 0,
    phi = 0,
    theta = 0;

  camera = new THREE.PerspectiveCamera(
    75,
    container.offsetWidth / container.offsetHeight,
    1,
    1100
  );
  camera.target = new THREE.Vector3(0, 0, 0);

  scene = new THREE.Scene();

  var geometry = new THREE.SphereBufferGeometry(500, 60, 40);
  // invert the geometry on the x-axis so that all of the faces point inward
  geometry.scale(-1, 1, 1);

  var texture = new THREE.TextureLoader().load(obj);
  var material = new THREE.MeshBasicMaterial({ map: texture });

  mesh = new THREE.Mesh(geometry, material);

  scene.add(mesh);

  renderer = new THREE.WebGLRenderer();
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(container.offsetWidth, container.offsetHeight);
  container.appendChild(renderer.domElement);

  var animate = function () {
    requestAnimationFrame(animate);

    //update/animation
    // if (isUserInteracting === false) {
    //     lon += 0.1;
    // }

    lat = Math.max(-85, Math.min(85, lat));
    // phi = THREE.Math.degToRad(90 - lat);
    // theta = THREE.Math.degToRad(lon);

    phi = degrees_to_radians(90 - lat);
    theta = degrees_to_radians(lon);

    camera.target.x = 500 * Math.sin(phi) * Math.cos(theta);
    camera.target.y = 500 * Math.cos(phi);
    camera.target.z = 500 * Math.sin(phi) * Math.sin(theta);

    camera.lookAt(camera.target);

    /*
    // distortion
    camera.position.copy( camera.target ).negate();
    */

    renderer.render(scene, camera);
  };

  animate();

  addListenerMulti(container, "mousedown touchstart", function (event) {
    // console.log("start");
    isUserInteracting = true;

    var clientX = event.clientX || event.touches[0].clientX;
    var clientY = event.clientY || event.touches[0].clientY;

    onMouseDownMouseX = clientX;
    onMouseDownMouseY = clientY;

    onMouseDownLon = lon;
    onMouseDownLat = lat;
  });

  addListenerMulti(container, "mousemove touchmove", function (event) {
    // console.log("move");
    if (isUserInteracting === true) {
      var clientX = event.clientX || event.touches[0].clientX;
      var clientY = event.clientY || event.touches[0].clientY;

      lon = (onMouseDownMouseX - clientX) * 0.1 + onMouseDownLon;
      lat = (clientY - onMouseDownMouseY) * 0.1 + onMouseDownLat;
    }
  });

  addListenerMulti(container, "mouseup touchend", function () {
    // console.log("end");
    isUserInteracting = false;
  });

  window.addEventListener(
    "resize",
    function () {
      camera.aspect = container.offsetWidth / container.offsetHeight;
      camera.updateProjectionMatrix();

      renderer.setSize(container.offsetWidth, container.offsetHeight);
    },
    false
  );
}

function initEquirectangular() {
  const viewers = document.querySelectorAll(".viewer--equirectangular");

  if (document.body.contains(viewers[0])) {
    viewers.forEach((viewer) => {
      let obj = viewer.dataset.obj;
      let btnViewer = viewer
        .closest(".component")
        .querySelectorAll(".btn__viewer")[0];

      viewer.addEventListener("click", function fn(e) {
        viewer.closest(".component").classList.add("viewer--active");
        setupEqui(viewer, obj);
        e.currentTarget.removeEventListener(e.type, fn);
      });
    });
  }
}

document.addEventListener("DOMContentLoaded", initEquirectangular);

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* Panorama Viewer */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

function initPano() {
  let panoramas = document.querySelectorAll(".viewer--panorama__wrapper");
  if (document.body.contains(panoramas[0])) {
    panoramas.forEach((panorama) => {
      let btnViewer = panorama.closest(".component"),
        imgPano = panorama.querySelectorAll("img")[0];

      let distCenter = imgPano.offsetWidth / 2 - window.innerWidth / 2;

      btnViewer.addEventListener("click", function fn(e) {
        panorama.closest(".component").classList.add("viewer--active");

        Draggable.create(panorama, {
          type: "scrollLeft",
          edgeResistance: 0.8,
          dragResistance: 0.05,
          throwProps: true,
          lockAxis: true,
          dragClickables: true,
        });
        TweenMax.to(panorama, 0, {
          scrollTo: { x: distCenter, autoKill: false },
          onStart: function () {},
          onComplete: function () {
            TweenMax.to(panorama, 1, {
              scrollTo: { x: 0, autoKill: false },
              ease: Power4.easeOut,
            });
          },
        });

        e.currentTarget.removeEventListener(e.type, fn);
      });
    });
  }
}

document.addEventListener("DOMContentLoaded", initPano);

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/* Accordion(s) */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

function initAccordions() {
  let accordionToggles = document.querySelectorAll(".accordion__toggle");
  if (accordionToggles !== null) {
    accordionToggles.forEach((accordionToggle) =>
      accordionToggle.addEventListener("click", function () {
        toggleAccordion(accordionToggle.closest(".accordion"), accordionToggle);
      })
    );
  }
}

function toggleAccordion(accordion, accordionToggle) {
  let accordionContent = accordion.querySelectorAll(".accordion__content")[0];

  if (accordionContent.classList.contains("accordion__content--hidden")) {
    if (
      accordionToggle !== undefined &&
      accordionToggle.dataset.close !== undefined
    ) {
      accordionToggle.innerHTML = accordionToggle.dataset.close;
    }
    openAccordion(accordion, accordionToggle);
  } else {
    closeAccordion(accordion, accordionToggle);
    if (
      accordionToggle !== undefined &&
      accordionToggle.dataset.open !== undefined
    ) {
      accordionToggle.innerHTML = accordionToggle.dataset.open;
    }
  }
}

function openAccordion(accordion, accordionToggle) {
  let accordionContent = accordion.querySelectorAll(".accordion__content")[0];

  accordion.classList.add("accordion--visible");
  accordion.classList.remove("accordion--hidden");

  TweenMax.to(accordionContent, 0.3, {
    css: { className: "-=accordion__content--hidden" },
    ease: Power4.easeInOut,
    onComplete: function () {
      TweenMax.to(accordionContent, 0.3, { opacity: 1 });
    },
  });
  accordionContent.classList.add("accordion__content--visible");
}

function closeAccordion(accordion, accordionToggle) {
  let accordionContent = accordion.querySelectorAll(".accordion__content")[0];
  accordionContent.classList.remove("accordion__item--visible");
  TweenMax.to(accordionContent, 0.1, { opacity: 0, ease: Power4.easeOut });
  TweenMax.to(accordionContent, 0.3, {
    height: 0,
    ease: Power4.easeOut,
    delay: 0.1,
    onComplete: function () {
      accordion.classList.add("accordion--hidden");
      accordion.classList.remove("accordion--visible");
      accordionContent.setAttribute("style", "");
      accordionContent.classList.add("accordion__content--hidden");
      accordionContent.classList.remove("accordion__content--visible");
    },
  });
}

document.addEventListener("DOMContentLoaded", initAccordions);
